import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import useAccess from "../../hooks/useAccess";
import Pagination from "../../components/Pagination";
import useClientTableSort, {
  sortRowsByConfig,
} from "../../hooks/useClientTableSort";
import { buildCustomTaskPdfUrl } from "../../utils/customTaskPdf";
import "../../styles/Form.css";

const CUSTOM_TASKS_PER_PAGE = 10;
const DEFAULT_CONTENT_POSITION = "bottom";
const DEFAULT_FONT_FAMILY = "Calibri, Arial, sans-serif";
const DEFAULT_FONT_SIZE = "16";
const FONT_FAMILY_OPTIONS = [
  { value: "Calibri, Arial, sans-serif", label: "Calibri" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "Georgia, serif", label: "Georgia" },
];
const FONT_SIZE_OPTIONS = ["11", "12", "14", "16", "18", "24", "32"];

const emptyForm = {
  t_id: null,
  t_title: "",
  t_display_title: "",
  t_content: "",
  t_content_position: DEFAULT_CONTENT_POSITION,
  t_category_id: "",
  t_requires_ref: 0,
  t_order: 0,
  t_active: 1,
  t_default: 0,
  t_allow_upload: 0,
  t_show_expiry: 0,
  t_show_issue_date: 0,
  t_show_document_number: 0,
  t_show_card_number: 0,
  t_show_issue_country: 0,
  t_show_nationality: 0,
  t_show_list: 0,
  t_show_signature: 1,
  t_show_employee: 1,
  t_show_date: 1,
};

const CustomTaskCreateHelper = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskSearch, setTaskSearch] = useState("");
  const [currentTaskPage, setCurrentTaskPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [taskPdf, setTaskPdf] = useState(null);
  const [currentTaskPdf, setCurrentTaskPdf] = useState("");
  const [fontFamily, setFontFamily] = useState(DEFAULT_FONT_FAMILY);
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [textColor, setTextColor] = useState("#000000");
  const editorRef = useRef(null);
  const tasksTableRef = useRef(null);

  const { hasPermission } = useAccess();

  const PERMISSIONS = {
    customTask: {
      view:
        hasPermission("AccountSetting", "Custom Task", "view") ||
        hasPermission("AccountSetting", "Custom Task", "edit"),
      edit: hasPermission("AccountSetting", "Custom Task", "edit"),
    },
  };

  const isEditMode = !!form.t_id;
  const canEdit = PERMISSIONS.customTask.edit;
  const normalizedTaskSearch = taskSearch.trim().toLowerCase();
  const filteredTasks = normalizedTaskSearch
    ? tasks.filter((task) =>
        [
          task.t_display_title,
          task.t_title,
          task.t_key,
          task.t_route,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(normalizedTaskSearch)
        )
      )
    : tasks;
  const taskSortConfig = useClientTableSort(tasksTableRef, () =>
    setCurrentTaskPage(1)
  );
  const sortedTasks = sortRowsByConfig(filteredTasks, taskSortConfig, [
    (task) => task.t_display_title || task.t_title,
    (task) => task.t_key,
    (task) => task.t_route,
    (task) => task.t_active,
  ]);
  const paginatedTasks = sortedTasks.slice(
    (currentTaskPage - 1) * CUSTOM_TASKS_PER_PAGE,
    currentTaskPage * CUSTOM_TASKS_PER_PAGE
  );

  const syncEditorContent = useCallback((html) => {
    const editor = editorRef.current;
    const nextHtml = html || "";
    if (editor && editor.innerHTML !== nextHtml) {
      editor.innerHTML = nextHtml;
    }
  }, []);

  const updateContent = useCallback((nextContent) => {
    setForm((prev) => ({ ...prev, t_content: nextContent }));
  }, []);

  const handleEditorInput = useCallback(() => {
    updateContent(editorRef.current?.innerHTML || "");
  }, [updateContent]);

  const normalizeFontFamilyValue = useCallback((fontFamilyValue) => {
    const normalized = (fontFamilyValue || "").toLowerCase();

    if (normalized.includes("calibri")) {
      return "Calibri, Arial, sans-serif";
    }
    if (normalized.includes("times new roman")) {
      return "'Times New Roman', Times, serif";
    }
    if (normalized.includes("georgia")) {
      return "Georgia, serif";
    }
    if (normalized.includes("arial")) {
      return "Arial, Helvetica, sans-serif";
    }

    return DEFAULT_FONT_FAMILY;
  }, []);

  const normalizeFontSizeValue = useCallback((fontSizeValue) => {
    const parsed = Number.parseInt(fontSizeValue || "", 10);
    if (!parsed) {
      return DEFAULT_FONT_SIZE;
    }

    if (FONT_SIZE_OPTIONS.includes(String(parsed))) {
      return String(parsed);
    }

    return FONT_SIZE_OPTIONS.reduce((closest, option) => {
      const currentDistance = Math.abs(Number(option) - parsed);
      const closestDistance = Math.abs(Number(closest) - parsed);
      return currentDistance < closestDistance ? option : closest;
    }, DEFAULT_FONT_SIZE);
  }, []);

  const syncToolbarState = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const selection = window.getSelection();
    let styleSource = editor;

    if (
      selection &&
      selection.rangeCount > 0 &&
      editor.contains(selection.getRangeAt(0).commonAncestorContainer)
    ) {
      const range = selection.getRangeAt(0);
      const container =
        range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
          ? range.commonAncestorContainer
          : range.commonAncestorContainer.parentElement;

      if (container && editor.contains(container)) {
        styleSource = container;
      }
    } else {
      const firstStyledElement = editor.querySelector("[style]");
      if (firstStyledElement) {
        styleSource = firstStyledElement;
      }
    }

    const computed = window.getComputedStyle(styleSource);
    setFontFamily(normalizeFontFamilyValue(computed.fontFamily));
    setFontSize(normalizeFontSizeValue(computed.fontSize));
    setTextColor(computed.color || "#000000");
  }, [normalizeFontFamilyValue, normalizeFontSizeValue]);

  const normalizePastedContent = useCallback((root) => {
    if (!root) return;

    root.querySelectorAll("font").forEach((element) => {
      const parent = element.parentNode;
      if (!parent) return;

      while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
      }

      parent.removeChild(element);
    });

    const elements = [root, ...root.querySelectorAll("*")];
    elements.forEach((element) => {
      element.removeAttribute("class");
      element.removeAttribute("face");
      element.removeAttribute("size");
      element.removeAttribute("color");

      if (!element.style) return;

      element.style.removeProperty("font-family");
      element.style.removeProperty("font-size");
      element.style.removeProperty("color");
      element.style.removeProperty("line-height");
      element.style.removeProperty("background");
      element.style.removeProperty("background-color");
      element.style.removeProperty("mso-highlight");

      if (!element.getAttribute("style")) {
        element.removeAttribute("style");
      }
    });
  }, []);

  useEffect(() => {
    syncEditorContent(form.t_content);
    syncToolbarState();
  }, [form.t_content, syncEditorContent, syncToolbarState]);

  const getEditorRange = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return null;

    editor.focus();
    const selection = window.getSelection();
    if (!selection) return null;

    if (selection.rangeCount > 0) {
      const existingRange = selection.getRangeAt(0);
      if (editor.contains(existingRange.commonAncestorContainer)) {
        return { selection, range: existingRange };
      }
    }

    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    return { selection, range };
  }, []);

  const insertHtmlAtCursor = useCallback(
    (html) => {
      const editor = editorRef.current;
      if (!editor) {
        setForm((prev) => ({ ...prev, t_content: `${prev.t_content}${html}` }));
        return;
      }

      const rangeState = getEditorRange();
      if (!rangeState) return;

      const { selection, range } = rangeState;
      range.deleteContents();

      const template = document.createElement("template");
      template.innerHTML = html;
      const fragment = template.content.cloneNode(true);
      const lastNode = fragment.lastChild;
      range.insertNode(fragment);

      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      handleEditorInput();
      syncToolbarState();
    },
    [getEditorRange, handleEditorInput, syncToolbarState],
  );

  const insertContentToken = (tokenValue) => {
    insertHtmlAtCursor(tokenValue);
  };

  const insertEmployeeName = () => {
    insertContentToken("{{employee_name}}");
  };

  const insertEmployeePosition = () => {
    insertContentToken("{{text_field:employee_position}}");
  };

  const insertTextField = () => {
    insertContentToken(`{{text_field:field_${Date.now()}}}`);
  };

  const insertDateField = () => {
    insertContentToken("{{date_field:current_date}}");
  };

  const execEditorCommand = useCallback(
    (command, value = null) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.focus();
      document.execCommand("styleWithCSS", false, true);
      document.execCommand(command, false, value);
      handleEditorInput();
      syncToolbarState();
    },
    [handleEditorInput, syncToolbarState],
  );

  const applyStyleToSelectionOrAll = useCallback(
    (styleUpdates) => {
      const editor = editorRef.current;
      if (!editor) return;

      const selection = window.getSelection();
      const hasSelection =
        selection &&
        selection.rangeCount > 0 &&
        !selection.isCollapsed &&
        editor.contains(selection.getRangeAt(0).commonAncestorContainer);

      if (hasSelection) {
        const range = selection.getRangeAt(0);
        const span = document.createElement("span");
        Object.entries(styleUpdates).forEach(([key, value]) => {
          span.style[key] = value;
        });

        try {
          range.surroundContents(span);
        } catch {
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      } else {
        const elements = [editor, ...editor.querySelectorAll("*")];
        elements.forEach((element) => {
          Object.entries(styleUpdates).forEach(([key, value]) => {
            element.style[key] = value;
          });
        });
      }

      handleEditorInput();
      syncToolbarState();
    },
    [handleEditorInput, syncToolbarState],
  );

  const applyFontSize = useCallback(
    (sizePx) => {
      if (!sizePx) return;
      applyStyleToSelectionOrAll({ fontSize: `${sizePx}px` });
    },
    [applyStyleToSelectionOrAll],
  );

  const applyFontFamily = useCallback(
    (nextFontFamily) => {
      if (!nextFontFamily) return;
      applyStyleToSelectionOrAll({ fontFamily: nextFontFamily });
    },
    [applyStyleToSelectionOrAll],
  );

  const insertTable = () => {
    const rowInput = window.prompt("How many rows?", "3");
    const colInput = window.prompt("How many columns?", "5");
    const rows = Number.parseInt(rowInput || "", 10);
    const cols = Number.parseInt(colInput || "", 10);

    if (!rows || !cols || rows < 1 || cols < 1) {
      return;
    }

    const tableRows = Array.from({ length: rows }, (_, rowIndex) => {
      const cellTag = rowIndex === 0 ? "th" : "td";
      const cells = Array.from({ length: cols }, (_, colIndex) => {
        const label =
          rowIndex === 0 ? `Column ${colIndex + 1}` : `Value ${colIndex + 1}`;
        return `<${cellTag}>${label}</${cellTag}>`;
      }).join("");
      return `<tr>${cells}</tr>`;
    }).join("");

    insertHtmlAtCursor(`<table><tbody>${tableRows}</tbody></table><p><br></p>`);
  };

  const handleEditorPaste = useCallback(
    (e) => {
      e.preventDefault();

      const clipboard = e.clipboardData;
      const html = clipboard?.getData("text/html");
      const text = clipboard?.getData("text/plain") || "";

      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        normalizePastedContent(doc.body);
        insertHtmlAtCursor(doc.body.innerHTML);
        return;
      }

      insertHtmlAtCursor(text.replace(/\n/g, "<br>"));
    },
    [insertHtmlAtCursor, normalizePastedContent],
  );

  const loadTasks = useCallback(() => {
    axios
      .get(`${API_BASE}/api/custom-tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setTasks(res.data))
      .catch(() => alert("Failed to load custom tasks"));
  }, [API_BASE, token]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/custom-tasks/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCategories(res.data))
      .catch(() => alert("Failed to load categories"));

    loadTasks();
  }, [API_BASE, token, loadTasks]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === "t_category_id") {
        const selectedCategoryId = Number(value);
        const sameCategoryTasks = tasks.filter(
          (task) => Number(task.t_category_id) === selectedCategoryId,
        );
        const maxOrder = sameCategoryTasks.reduce((max, task) => {
          const taskOrder = Number(task.t_order) || 0;
          return taskOrder > max ? taskOrder : max;
        }, 0);

        return {
          ...prev,
          [name]: value,
          t_order: value ? maxOrder + 5 : 0,
        };
      }

      if (name === "t_show_list") {
        const nextShowList = Number(value);
        return {
          ...prev,
          [name]: value,
          t_requires_ref: nextShowList === 1 ? 1 : prev.t_requires_ref,
          t_default: nextShowList === 1 ? 1 : prev.t_default,
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setTaskPdf(null);
    setCurrentTaskPdf("");
    setFontFamily(DEFAULT_FONT_FAMILY);
    setFontSize(DEFAULT_FONT_SIZE);
    setTextColor("#000000");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setLoading(true);

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });

      if (taskPdf) {
        formData.append("t_pdf", taskPdf);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      if (isEditMode) {
        await axios.put(
          `${API_BASE}/api/custom-tasks/${form.t_id}`,
          formData,
          config,
        );
        alert("Custom Task updated");
      } else {
        await axios.post(`${API_BASE}/api/custom-tasks`, formData, config);
        alert("Custom Task created");
      }

      resetForm();
      loadTasks();
    } catch (err) {
      console.error(err);
      alert("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (task) => {
    if (!canEdit) return;
    const normalizedShowList = Number(task.t_show_list ?? 0);
    setForm({
      t_id: task.t_id,
      t_title: task.t_title,
      t_display_title: task.t_display_title,
      t_content: task.t_content || "",
      t_content_position: task.t_content_position || DEFAULT_CONTENT_POSITION,
      t_category_id: task.t_category_id,
      t_requires_ref: normalizedShowList === 1 ? 1 : task.t_requires_ref,
      t_order: task.t_order,
      t_active: task.t_active,
      t_default: normalizedShowList === 1 ? 1 : task.t_default,
      t_allow_upload: task.t_allow_upload ?? 0,
      t_show_expiry: task.t_show_expiry ?? 0,
      t_show_issue_date: task.t_show_issue_date ?? 0,
      t_show_document_number: task.t_show_document_number ?? 0,
      t_show_card_number: task.t_show_card_number ?? 0,
      t_show_issue_country: task.t_show_issue_country ?? 0,
      t_show_nationality: task.t_show_nationality ?? 0,
      t_show_list: normalizedShowList,
      t_show_signature: task.t_show_signature ?? 1,
      t_show_employee: task.t_show_employee ?? 1,
      t_show_date: task.t_show_date ?? 1,
    });
    setTaskPdf(null);
    setCurrentTaskPdf(task.t_pdf || "");
    setFontFamily(DEFAULT_FONT_FAMILY);
    setFontSize(DEFAULT_FONT_SIZE);
    setTextColor("#000000");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="form-wrapper my-2">
      <h2>{isEditMode ? "Edit Custom Task" : "Create Custom Task"}</h2>

      <form onSubmit={handleSubmit}>
        <label className="form-label mt-3">Admin Title</label>
        <input
          className="form-control mb-3"
          name="t_title"
          value={form.t_title}
          disabled={!canEdit}
          onChange={handleChange}
          required
        />

        <label className="form-label">Display Title</label>
        <input
          className="form-control mb-3"
          name="t_display_title"
          value={form.t_display_title}
          disabled={!canEdit}
          onChange={handleChange}
          required
        />

        <label className="form-label">Category</label>
        <select
          className="form-control mb-3"
          name="t_category_id"
          value={form.t_category_id}
          disabled={!canEdit}
          onChange={handleChange}
          required
        >
          <option value="">-- Select Category --</option>
          {categories.map((category) => (
            <option key={category.tc_id} value={category.tc_id}>
              {category.tc_name}
            </option>
          ))}
        </select>

        <div className="row mt-3">
          <div className="col-md-4 mb-3">
            <label>Requires Ref</label>
            <select
              className="form-control"
              name="t_requires_ref"
              value={form.t_requires_ref}
              disabled={!canEdit || Number(form.t_show_list) === 1}
              onChange={handleChange}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </select>
            {Number(form.t_show_list) === 1 && (
              <small className="text-muted">
                Required automatically when Show List is on.
              </small>
            )}
          </div>

          <div className="col-md-4 mb-3">
            <label>Show List</label>
            <select
              className="form-control"
              name="t_show_list"
              value={form.t_show_list}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Default</label>
            <select
              className="form-control"
              name="t_default"
              value={form.t_default}
              disabled={!canEdit || Number(form.t_show_list) === 1}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
            {Number(form.t_show_list) === 1 && (
              <small className="text-muted">
                Turned on automatically when Show List is on.
              </small>
            )}
          </div>

          <div className="col-md-4 mb-3">
            <label>Allow Upload</label>
            <select
              className="form-control"
              name="t_allow_upload"
              value={form.t_allow_upload}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Order</label>
            <input
              type="number"
              className="form-control"
              name="t_order"
              value={form.t_order}
              disabled={!canEdit}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="col-md-4 mb-3">
            <label>Active</label>
            <select
              className="form-control"
              name="t_active"
              value={form.t_active}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Expiry Date</label>
            <select
              className="form-control"
              name="t_show_expiry"
              value={form.t_show_expiry}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Issue Date</label>
            <select
              className="form-control"
              name="t_show_issue_date"
              value={form.t_show_issue_date}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Document Number</label>
            <select
              className="form-control"
              name="t_show_document_number"
              value={form.t_show_document_number}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Card Number</label>
            <select
              className="form-control"
              name="t_show_card_number"
              value={form.t_show_card_number}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Issue Country</label>
            <select
              className="form-control"
              name="t_show_issue_country"
              value={form.t_show_issue_country}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Nationality</label>
            <select
              className="form-control"
              name="t_show_nationality"
              value={form.t_show_nationality}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Signature</label>
            <select
              className="form-control"
              name="t_show_signature"
              value={form.t_show_signature}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Employee Name</label>
            <select
              className="form-control"
              name="t_show_employee"
              value={form.t_show_employee}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Show Date</label>
            <select
              className="form-control"
              name="t_show_date"
              value={form.t_show_date}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col-md-4 mb-3">
            <label>Content Position With PDF</label>
            <select
              className="form-control"
              name="t_content_position"
              value={form.t_content_position}
              disabled={!canEdit}
              onChange={handleChange}
            >
              <option value="bottom">Content Below PDF</option>
              <option value="top">Content Above PDF</option>
            </select>
          </div>
        </div>

        <label className="form-label mt-3">Task PDF (optional)</label>
        <input
          type="file"
          accept="application/pdf"
          className="form-control mb-2"
          disabled={!canEdit}
          onChange={(e) => setTaskPdf(e.target.files[0])}
        />
        {currentTaskPdf && (
          <div className="mb-2">
            <small className="text-muted d-block">Current PDF</small>
            <a
              href={buildCustomTaskPdfUrl(API_BASE, currentTaskPdf)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {currentTaskPdf.split("/").pop()}
            </a>
          </div>
        )}

        {(taskPdf || currentTaskPdf) && (
          <small className="text-muted d-block mb-2">
            Use the content position setting to show the task content above or below the PDF.
          </small>
        )}

        <label className="form-label mt-3">Task Content</label>
        <div className="custom-task-toolbar mb-2">
          <select
            className="form-control form-control-sm"
            style={{ width: 160 }}
            disabled={!canEdit}
            value={fontFamily}
            onChange={(e) => {
              setFontFamily(e.target.value);
              applyFontFamily(e.target.value);
            }}
          >
            {FONT_FAMILY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="form-control form-control-sm"
            style={{ width: 90 }}
            disabled={!canEdit}
            value={fontSize}
            onChange={(e) => {
              setFontSize(e.target.value);
              applyFontSize(e.target.value);
            }}
          >
            <option value="11">11</option>
            <option value="12">12</option>
            <option value="14">14</option>
            <option value="16">16</option>
            <option value="18">18</option>
            <option value="24">24</option>
            <option value="32">32</option>
          </select>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={() => execEditorCommand("bold")}
          >
            Bold
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={() => execEditorCommand("italic")}
          >
            Italic
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={() => execEditorCommand("underline")}
          >
            Underline
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={() => execEditorCommand("insertOrderedList")}
          >
            Numbered List
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={() => execEditorCommand("insertUnorderedList")}
          >
            Bullet List
          </button>
          <input
            type="color"
            className="form-control form-control-color"
            title="Text Color"
            disabled={!canEdit}
            value={textColor}
            onChange={(e) => {
              setTextColor(e.target.value);
              execEditorCommand("foreColor", e.target.value);
            }}
          />
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={insertEmployeeName}
          >
            Insert Employee Name
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={insertEmployeePosition}
          >
            Employee Position
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={insertTextField}
          >
            Insert Text Field
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={insertDateField}
          >
            Insert Date
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canEdit}
            onClick={insertTable}
          >
            Insert Table
          </button>
        </div>
        <div className="custom-task-editor-help text-muted mb-2">
          Pasted content keeps its structure, but uses the default editor font and size unless you change it here.
        </div>
        <div
          ref={editorRef}
          className="custom-task-html-editor"
          contentEditable={canEdit}
          suppressContentEditableWarning
          onPaste={handleEditorPaste}
          onInput={handleEditorInput}
          onBlur={handleEditorInput}
          onClick={syncToolbarState}
          onKeyUp={syncToolbarState}
          onMouseUp={syncToolbarState}
        />

        <button className="btn btn-primary mt-4" disabled={loading || !canEdit}>
          {loading ? "Saving..." : isEditMode ? "Save" : "Create"}
        </button>

        {isEditMode && canEdit && (
          <button
            type="button"
            className="btn btn-secondary mt-4 ms-2"
            onClick={resetForm}
          >
            Cancel Edit
          </button>
        )}
      </form>

      <hr className="my-5" />

      <h4>Customized Tasks</h4>

      <div className="input-group mb-3" style={{ maxWidth: "520px" }}>
        <span className="input-group-text" aria-hidden="true">
          <i className="fa fa-search" />
        </span>
        <input
          type="search"
          className="form-control"
          value={taskSearch}
          onChange={(event) => {
            setTaskSearch(event.target.value);
            setCurrentTaskPage(1);
          }}
          placeholder="Search by title, key, or route..."
          aria-label="Search customized tasks"
        />
        {taskSearch && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              setTaskSearch("");
              setCurrentTaskPage(1);
            }}
          >
            Clear
          </button>
        )}
      </div>

      <table
        ref={tasksTableRef}
        className="table table-bordered"
        data-nw-client-sort="true"
      >
        <thead>
          <tr>
            <th>Title</th>
            <th>Key</th>
            <th>Route</th>
            <th>Active</th>
            <th width="100">Action</th>
          </tr>
        </thead>
        <tbody>
          {paginatedTasks.map((task) => (
            <tr key={task.t_id}>
              <td>{task.t_display_title}</td>
              <td>{task.t_key}</td>
              <td>{task.t_route}</td>
              <td>{task.t_active ? "Yes" : "No"}</td>
              <td>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleEdit(task)}
                  disabled={loading || !canEdit}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}

          {!filteredTasks.length && (
            <tr>
              <td colSpan="5" className="text-center">
                {tasks.length
                  ? "No custom tasks match your search"
                  : "No custom tasks found"}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {filteredTasks.length > 0 && (
        <Pagination
          currentPage={currentTaskPage}
          totalItems={filteredTasks.length}
          itemsPerPage={CUSTOM_TASKS_PER_PAGE}
          onPageChange={setCurrentTaskPage}
          scrollToTopOnChange={false}
        />
      )}
    </div>
  );
};

export default CustomTaskCreateHelper;
